import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { Fragment, useMemo, useState } from 'react'
import SizeTable from 'components/common/size-table'
import ResponsiveImage from 'components/images/responsive-image'
import {
  MEASUREMENT_DATA,
  MEASUREMENT_TYPE,
} from 'components/pdp/interactions/measurements/const'
import { SHOE_TABLE_HEADER } from 'components/pdp/interactions/measurements/sizes-guide/const'
import SizesGuidesUnits from 'components/pdp/interactions/measurements/sizes-guide-units'
import {
  getColumnsWithInjectWidth,
  getTableRows,
  translateMeasurementType,
} from 'components/pdp/interactions/measurements/utils'
import env from 'config/env'
import Typo from 'constants/typography'
import { withI18next } from 'lib/i18n/withI18next'
import { hasLocizeTranslation } from 'lib/utils/locize'
import Tabs from 'components/common/tabs'
import BodySizeTable from '../body-measurement'
import ConversionTable from '../conversion-table'
import styles from './styles.scss'

const DOUBLE_TABLE_SECTION_TABS_ENUM = {
  CONVERSION: 'CONVERSION',
  BODY_MEASUREMENTS: 'BODY_MEASUREMENTS',
  PRODUCT_MEASUREMENTS: 'PRODUCT_MEASUREMENTS',
}

const DoubleTableSection = ({
  isPmloDualTable,
  isShoeDualTable,
  openModal,
  product,
  t,
}) => {
  const [unit, setUnit] = useState('in')
  const [selectedTab, setSelectedTab] = useState(null)
  const {
    attributes,
    is_new_size_method: isNewSize = false,
    measurement_image,
    measurement_spec,
  } = product

  const simplifiedSpec = isShoeDualTable
    ? measurement_spec?.map((item) => item.is_fix_unit && item.name)
    : null
  const customFirstTableHeader = isShoeDualTable ? SHOE_TABLE_HEADER : null

  const rows = useMemo(
    () =>
      getTableRows(
        attributes,
        t,
        MEASUREMENT_TYPE.product,
        unit,
        simplifiedSpec,
        isShoeDualTable,
      ),
    [attributes, isShoeDualTable, simplifiedSpec, t, unit],
  )
  const columns = useMemo(
    () =>
      getColumnsWithInjectWidth(
        rows,
        customFirstTableHeader,
        attributes,
        isShoeDualTable,
      ),
    [attributes, customFirstTableHeader, isShoeDualTable, rows],
  )
  const isRowEmpty = rows.length === 0

  const getDoubleTableSectionTabs = () => {
    const DOUBLE_TABLE_SECTION_TABS = [
      ...(isShoeDualTable
        ? [
            {
              label: hasLocizeTranslation(
                t,
                'PDP_CONVERSION_CHART',
                'Conversion Size Chart',
              ),
              status: DOUBLE_TABLE_SECTION_TABS_ENUM.CONVERSION,
              contents: (
                <ConversionTable
                  product={product}
                  simplifiedSpec={simplifiedSpec}
                />
              ),
            },
          ]
        : []),
      ...(isPmloDualTable
        ? [
            {
              label: translateMeasurementType(MEASUREMENT_TYPE.body, t),
              status: DOUBLE_TABLE_SECTION_TABS_ENUM.BODY_MEASUREMENTS,
              contents: (
                <BodySizeTable
                  openModal={openModal}
                  product={product}
                  unit={unit}
                />
              ),
            },
          ]
        : []),
      ...(!isRowEmpty
        ? [
            {
              label: translateMeasurementType(MEASUREMENT_TYPE.product, t),
              status: DOUBLE_TABLE_SECTION_TABS_ENUM.PRODUCT_MEASUREMENTS,
              contents: (
                <Fragment>
                  <div
                    className={clsx('pdp-measurements__table', {
                      'for-shoe': isShoeDualTable,
                    })}
                  >
                    <SizeTable
                      columns={columns}
                      rows={rows}
                      isNewSize={isNewSize}
                      isPhone
                    />
                  </div>
                  <ResponsiveImage
                    src={`${env.IMG_HOST}${measurement_image?.path}`}
                    tablet="40vw"
                    laptop="40vw"
                    desktop="40vw"
                    bigDesktop="40vw"
                  />
                </Fragment>
              ),
            },
          ]
        : []),
    ]

    return DOUBLE_TABLE_SECTION_TABS
  }

  const tabs = getDoubleTableSectionTabs()
  const tabIndexNumber = Math.max(
    0,
    tabs.findIndex((tab) => tab.status === selectedTab),
  )

  if (!tabs.length) return null

  return (
    <Fragment>
      <style jsx>{styles}</style>
      <SizesGuidesUnits unit={unit} onClick={(val) => setUnit(val)} t={t} />
      <Tabs
        tabs={tabs}
        selectedIndex={tabIndexNumber}
        className="pdp-measurements__tab"
        onChange={(tabIndex) => setSelectedTab(tabs[tabIndex].status)}
      />
      {tabs[tabIndexNumber].contents}
      {isNewSize && (
        <div className={clsx('size-guide-text', Typo.body2)}>
          {hasLocizeTranslation(
            t,
            MEASUREMENT_DATA.resizeDataText.titleKey,
            MEASUREMENT_DATA.resizeDataText.default,
          )}
        </div>
      )}
    </Fragment>
  )
}

DoubleTableSection.propTypes = {
  isPmloDualTable: PropTypes.bool.isRequired,
  isShoeDualTable: PropTypes.bool.isRequired,
  openModal: PropTypes.func.isRequired,
  product: PropTypes.shape({
    attributes: PropTypes.arrayOf(
      PropTypes.shape({
        measurements: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            value: PropTypes.string,
            value_in: PropTypes.string,
          }),
        ),
        measurement_type_body: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            value: PropTypes.string,
            value_in: PropTypes.string,
          }),
        ),
      }),
    ),
    category: PropTypes.shape({
      id_category: PropTypes.number,
      link_rewrite: PropTypes.string,
    }),
    id_product: PropTypes.number,
    is_new_size_method: PropTypes.bool,
    measurement_image: PropTypes.shape({
      path: PropTypes.string,
    }),
    measurement_spec: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        is_fix_unit: PropTypes.bool,
      }),
    ),
    measurement_type: PropTypes.string,
    metadata: PropTypes.shape({
      size_recommendable: PropTypes.bool,
    }),
    model: PropTypes.shape({
      bust: PropTypes.number,
      height: PropTypes.number,
      hips: PropTypes.number,
      size: PropTypes.string,
      waist: PropTypes.number,
    }),
    prices: PropTypes.shape({
      original_price: PropTypes.number,
    }),
  }).isRequired,
  t: PropTypes.func.isRequired,
}

export default withI18next()(DoubleTableSection)
